/**
 * Axios instance configuration
 */

import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { API_BASE_URL, API_TIMEOUT } from '@/config/services';

// Serialize array params as repeated keys (FastAPI style)
function paramsSerializer(params: Record<string, unknown>): string {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach((v) => searchParams.append(key, String(v)));
    } else if (value !== null && value !== undefined) {
      searchParams.append(key, String(value));
    }
  });
  return searchParams.toString();
}

// Create axios instance
export const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: API_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  paramsSerializer: { serialize: paramsSerializer },
});

// Token storage (will be set by auth store)
let accessToken: string | null = null;
let refreshToken: string | null = null;
let onTokenRefresh: ((token: string) => void) | null = null;
let onAuthError: (() => void) | null = null;

// Set tokens from auth store
export function setAuthTokens(
  access: string | null,
  refresh: string | null,
  onRefresh?: (token: string) => void,
  onError?: () => void
) {
  accessToken = access;
  refreshToken = refresh;
  onTokenRefresh = onRefresh ?? null;
  onAuthError = onError ?? null;
}

// Clear tokens
export function clearAuthTokens() {
  accessToken = null;
  refreshToken = null;
  onTokenRefresh = null;
  onAuthError = null;
}

// Request interceptor - add auth header
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Use token from closure, or fallback to localStorage if not set yet
    let token = accessToken;
    if (!token) {
      try {
        const stored = localStorage.getItem('reinfo-auth');
        if (stored) {
          const parsed = JSON.parse(stored);
          token = parsed?.state?.accessToken || null;
        }
      } catch {
        // Ignore JSON parse errors
      }
    }
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - handle 401 and refresh token
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config;
    
    // If 401 and we have refresh token, try to refresh
    if (
      error.response?.status === 401 &&
      refreshToken &&
      originalRequest &&
      !(originalRequest as { _retry?: boolean })._retry
    ) {
      (originalRequest as { _retry?: boolean })._retry = true;
      
      try {
        // Try to refresh the token
        const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
          refresh_token: refreshToken,
        });
        
        const newAccessToken = response.data.access_token;
        const newRefreshToken = response.data.refresh_token;
        
        // Update tokens
        accessToken = newAccessToken;
        refreshToken = newRefreshToken;
        
        // Notify auth store
        if (onTokenRefresh) {
          onTokenRefresh(newAccessToken);
        }
        
        // Retry original request
        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        }
        
        return api(originalRequest);
      } catch (refreshError) {
        // Refresh failed, logout
        if (onAuthError) {
          onAuthError();
        }
        return Promise.reject(refreshError);
      }
    }
    
    return Promise.reject(error);
  }
);

export default api;
